<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import {
  getAdminStripeSettings,
  saveAdminStripeSettings,
  type AdminStripeSettings
} from "../admin-api";

const loading = ref(false);
const saving = ref(false);
const secretPreview = reactive({
  secretKey: "未配置",
  webhookSecret: "未配置"
});
const form = reactive({
  enabled: false,
  publishableKey: "",
  secretKey: "",
  webhookSecret: ""
});

onMounted(loadSettings);

async function loadSettings() {
  loading.value = true;
  try {
    applySettings(await getAdminStripeSettings());
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "Stripe 配置加载失败。");
  } finally {
    loading.value = false;
  }
}

async function submitSettings() {
  saving.value = true;
  try {
    const response = await saveAdminStripeSettings({
      enabled: form.enabled,
      publishableKey: form.publishableKey,
      secretKey: form.secretKey || undefined,
      webhookSecret: form.webhookSecret || undefined
    });
    message.success(response.message);
    form.secretKey = "";
    form.webhookSecret = "";
    await loadSettings();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "Stripe 配置保存失败。");
  } finally {
    saving.value = false;
  }
}

function applySettings(settings: AdminStripeSettings) {
  form.enabled = settings.enabled ?? false;
  form.publishableKey = settings.publishableKey ?? "";
  form.secretKey = "";
  form.webhookSecret = "";
  secretPreview.secretKey = settings.secretKey?.hasValue ? settings.secretKey.preview : "未配置";
  secretPreview.webhookSecret = settings.webhookSecret?.hasValue ? settings.webhookSecret.preview : "未配置";
}
</script>

<template>
  <a-card class="admin-settings-card" :loading="loading">
    <template #title>Stripe 支付配置</template>
    <template #extra>
      <a-tag :color="form.enabled ? 'green' : 'red'">{{ form.enabled ? "已启用" : "未启用" }}</a-tag>
    </template>

    <a-alert
      class="admin-form-alert"
      type="info"
      show-icon
      message="国际版 .net 域名会使用 Stripe Checkout。Secret Key 用来创建支付会话，Webhook Secret 用来校验 Stripe 回调。"
    />

    <a-form layout="vertical" :model="form" @finish="submitSettings">
      <a-form-item>
        <a-switch v-model:checked="form.enabled" />
        <span style="margin-left: 10px">启用 Stripe 支付</span>
      </a-form-item>

      <a-form-item label="Publishable Key">
        <a-input v-model:value="form.publishableKey" placeholder="pk_live_..." />
      </a-form-item>

      <a-row :gutter="16">
        <a-col :xs="24" :md="12">
          <a-form-item label="Secret Key">
            <a-input-password v-model:value="form.secretKey" :placeholder="`留空不修改，当前：${secretPreview.secretKey}`" />
          </a-form-item>
        </a-col>
        <a-col :xs="24" :md="12">
          <a-form-item label="Webhook Secret">
            <a-input-password v-model:value="form.webhookSecret" :placeholder="`留空不修改，当前：${secretPreview.webhookSecret}`" />
          </a-form-item>
        </a-col>
      </a-row>

      <a-button type="primary" html-type="submit" :loading="saving">保存 Stripe 配置</a-button>
    </a-form>
  </a-card>
</template>
